/**
 * src/services/webrtcBroadcastService.ts
 *
 * Client driver for the broadcaster-one-to-many signaling hub at
 * `/ws/webrtc` (see server/ws/webrtcHub.js).  Topology:
 *
 *   - Each broadcaster creates a fresh `RTCPeerConnection` per viewer.
 *   - Viewers request a connection by broadcasterId; the server forwards
 *     offer/answer/ice between the parties but never touches media.
 *
 * The service exposes two factories:
 *
 *   - `startBroadcast(stream, opts)` — returns a controller the caller can
 *     later `.stop()`.  New viewer join events spin up a per-viewer
 *     `RTCPeerConnection`.
 *
 *   - `joinBroadcast(broadcasterId, opts)` — returns a viewer controller
 *     whose `stream` property yields the received MediaStream once the
 *     connection is established.
 *
 * Country policy gating and deny-list enforcement are handled server-side
 * — the hub responds with `{type: 'denied', reason}` before any WebRTC
 * payload is exchanged.
 */

const DEFAULT_BACKEND = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
) as string;

/**
 * Feature flag — when `REACT_APP_USE_SFU=true`, startBroadcast /
 * joinBroadcast delegate to `src/services/sfuClient.ts` which
 * routes media through the mediasoup SFU on the dedicated VM.
 * Inlined here (rather than imported from ./sfuClient) to avoid
 * a circular module dependency — sfuClient imports this file's
 * types.  The corresponding server flag is `SFU_ENABLED=true`.
 */
/** True when the app is built to publish/subscribe via the mediasoup SFU (`REACT_APP_USE_SFU=true`). */
export function isSfuEnabled(): boolean {
  // IMPORTANT: access process.env.REACT_APP_USE_SFU DIRECTLY.  CRA /
  // webpack's DefinePlugin only substitutes literal `process.env.X`
  // references at build time; going through `const env = process.env`
  // leaves `env.REACT_APP_USE_SFU` as `undefined` at runtime in the
  // browser, so the SFU path never fires even when the env var is
  // baked into the bundle.
  // eslint-disable-next-line no-undef
  const v = typeof process !== 'undefined' ? process.env.REACT_APP_USE_SFU : undefined;
  return String(v || '').toLowerCase() === 'true';
}

function wsUrl(backendUrl: string): string {
  const origin = backendUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!origin) return '';
  return origin.replace(/^http/, 'ws') + '/ws/webrtc';
}

/**
 * ICE server configuration.
 *
 * STUN alone works when both peers have well-behaved NATs (home WiFi,
 * most desktop broadband).  For mobile carriers / corporate firewalls /
 * symmetric NATs, ICE cannot negotiate a media path over UDP and the
 * <video> tag stays black even though signaling succeeds.  A TURN
 * server is required to relay media in those cases.
 *
 * Priority order for configuration:
 *   1. REACT_APP_WEBRTC_ICE_SERVERS  — JSON array of RTCIceServer objects
 *                                       (full control; used when self-
 *                                       hosting CoTURN or using a
 *                                       commercial provider).
 *   2. REACT_APP_TURN_URL + REACT_APP_TURN_USERNAME + REACT_APP_TURN_CREDENTIAL
 *                                     — single-URL convenience.
 *   3. Defaults below — Google's public STUN plus the OpenRelay
 *                       free TURN pool (OK for demo / hackathon use,
 *                       should be replaced for production).
 */
function parseConfiguredIceServers(): RTCIceServer[] | null {
  try {
    const raw = (typeof process !== 'undefined' && (process as any).env?.REACT_APP_WEBRTC_ICE_SERVERS) || '';
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as RTCIceServer[];
  } catch { /* fall through to defaults */ }
  return null;
}

function defaultIceServers(): RTCIceServer[] {
  const env = (typeof process !== 'undefined' && (process as any).env) || {};
  const turnUrl = env.REACT_APP_TURN_URL;
  const turnUser = env.REACT_APP_TURN_USERNAME;
  const turnCred = env.REACT_APP_TURN_CREDENTIAL;
  const stunUrl = env.REACT_APP_STUN_URL || 'stun:stun.l.google.com:19302';

  // Start with the configured / Google STUN, then layer Cloudflare's
  // anycast STUN on top.  Cloudflare lives at IPs that basically
  // every network routes because they're intermingled with normal
  // HTTP traffic — this has proven reachable from Tesla's in-vehicle
  // Wi-Fi where arbitrary third-party STUN/TURN hostnames are
  // silently dropped.  Multiple STUN servers is explicitly allowed
  // by the ICE spec; the browser queries them in parallel.
  const servers: RTCIceServer[] = [
    { urls: stunUrl },
    { urls: 'stun:stun.cloudflare.com:3478' },
  ];
  if (turnUrl && turnUser && turnCred) {
    servers.push({ urls: turnUrl, username: turnUser, credential: turnCred });
    return servers;
  }
  // OpenRelay (free, rate-limited, demo-grade).  We list every
  // transport they offer — including the TURN-over-TLS URL on 443
  // — because some strict networks only let TLS out:
  //
  //   * 5G mobile carriers frequently block arbitrary UDP
  //   * Tesla's in-vehicle Wi-Fi tunnels everything through Starlink
  //     / LTE and drops UDP media traffic under load
  //   * Corporate / guest Wi-Fi commonly blocks ports 3478 + 5349
  //
  // Listing `turns:443?transport=tcp` first ensures ICE gathers a
  // TLS-relay candidate immediately, which mimics HTTPS closely
  // enough that almost every firewall permits it.
  servers.push({
    urls: [
      'turns:openrelay.metered.ca:443?transport=tcp',
      'turn:openrelay.metered.ca:443?transport=tcp',
      'turn:openrelay.metered.ca:443',
      'turn:openrelay.metered.ca:80',
    ],
    username: 'openrelayproject',
    credential: 'openrelayproject',
  });
  return servers;
}

const ICE_SERVERS: RTCIceServer[] = parseConfiguredIceServers() || defaultIceServers();

export function getIceServers(): RTCIceServer[] { return ICE_SERVERS.slice(); }

/**
 * Reorder an `RTCRtpCapabilities.codecs` array so H.264 variants come
 * first.
 *
 * iOS Safari is *technically* able to decode VP8 (since 12.2) and
 * VP9 (16+), but in the wild we see silent decode failures from
 * Chrome WebRTC peers that offer VP8/VP9 first — the track flows,
 * frames arrive, but the <video> shows black.  Forcing H.264 end-
 * to-end sidesteps the issue completely because H.264 has dedicated
 * hardware paths on every iPhone since the 4 and the Tesla
 * infotainment's embedded Chromium.  Applied to video transceivers on both broadcaster and
 * viewer so the SDP offer/answer converges on H.264 — which is the
 * only video codec with reliable hardware decode across iOS, macOS,
 * Android, Windows and Tesla's embedded Chromium.
 *
 * Non-video codecs are never reordered.  Unknown codecs are preserved
 * in their original order (after H.264) so we don't accidentally
 * drop a profile the browser considers essential.
 */
function preferH264(caps: RTCRtpCapabilities | null): RTCRtpCodec[] | null {
  if (!caps?.codecs) return null;
  const codecs = caps.codecs;
  const h264 = codecs.filter(c => /h264/i.test(c.mimeType));
  const rest = codecs.filter(c => !/h264/i.test(c.mimeType));
  if (h264.length === 0) return null; // H.264 not available — leave prefs untouched
  return [...h264, ...rest];
}

/**
 * Apply H.264-first codec preferences to every video transceiver on
 * the given RTCPeerConnection.  Silently no-ops on browsers that
 * don't support setCodecPreferences (old Firefox, some Chromium
 * forks).  Called once right after transceivers are created —
 * *before* createOffer / createAnswer — because the preference list
 * only takes effect when the next SDP is generated.
 */
function applyH264Preference(pc: RTCPeerConnection, reason: string): void {
  try {
    const caps = (RTCRtpReceiver as any)?.getCapabilities?.('video') as RTCRtpCapabilities | null;
    const sorted = preferH264(caps);
    if (!sorted) return;
    pc.getTransceivers().forEach((tr) => {
      const kind = tr.sender.track?.kind || tr.receiver.track?.kind;
      // Some browsers (older Safari) populate neither sender.track nor
      // receiver.track before negotiation — fall back on tr.mid string
      // heuristics isn't reliable, so we apply the preference to every
      // transceiver and let `setCodecPreferences` ignore kinds it
      // doesn't care about.
      if (kind && kind !== 'video') return;
      try { (tr as any).setCodecPreferences?.(sorted); }
      catch { /* some browsers throw if an audio transceiver receives video codecs */ }
    });
  } catch { /* ignore — not supported, fallback to browser default */ }
}

/** Viewer-side watchdog: if ICE doesn't reach `connected`/`completed`
 *  within this many ms we surface a friendly `ice_timeout` so the UI
 *  can render a retry prompt instead of leaving users staring at black. */
const VIEWER_ICE_TIMEOUT_MS = 20_000;

export interface BroadcasterOpts {
  broadcasterId: string;
  wallet?: string;
  country?: number;
  backendUrl?: string;
  /**
   * Identifier of the paid live-session record returned by
   * POST /api/live-sessions/request.  Required — the hub refuses any
   * broadcaster `hello` that doesn't present a matching grant.
   */
  liveSessionId: string;
  /** Short-lived HMAC grant issued by the backend. */
  grantToken: string;
  /**
   * Fires when a viewer joins.  The server-pushed `joined` event
   * carries the viewer's `wallet` and `country` in addition to
   * their peer id, which the broadcaster UI uses to build the
   * end-of-broadcast audience summary.  `peerId` stays the
   * primary key since the same wallet could conceivably join
   * from two devices and we want to track watch time per session,
   * not per human.
   */
  onViewerJoined?: (info: { peerId: string; wallet?: string; country?: number | null }) => void;
  onViewerLeft?: (peerId: string) => void;
  onDenied?: (reason: string) => void;
  onError?: (err: Error) => void;
  /**
   * Fired when the server ends the session (duration expired, admin
   * termination, policy change, etc.).  The reason matches the ended
   * reason in `LiveSession.endedReason`.
   */
  onSessionEnded?: (reason: string) => void;
  /**
   * Fired when the server pushes a `session_extended` event — i.e.
   * the broadcaster paid again or a viewer tipped to add time.  The
   * new scheduled end is authoritative; the UI should update its
   * countdown against this value immediately.
   */
  onSessionExtended?: (info: {
    scheduledEndAt: string;
    extensionSeconds?: number;
    extensionCount?: number;
    fromViewer?: boolean;
  }) => void;
  /**
   * Fired whenever the server-authoritative viewer count changes.  The
   * hub emits a `viewers_update` event every time a viewer joins or
   * leaves the room so both sides (broadcaster + viewers) can render
   * the same number without per-peer bookkeeping.
   */
  onViewersUpdate?: (count: number) => void;

  /**
   * Called when the signaling socket drops unexpectedly (not from a
   * user-initiated stop).  Return `true` to have the service keep
   * retrying the WebSocket — typically the caller checks that the
   * paid session still has remaining time.  Default: always `false`
   * (no auto-reconnect — behaviour unchanged from before reconnect
   * support landed).
   */
  shouldResume?: () => boolean;
  /**
   * Fired each time the service opens a fresh signaling socket after
   * an unexpected drop.  `attempt` starts at 1.  UIs typically flip a
   * "Reconnecting…" overlay on.
   */
  onReconnecting?: (attempt: number) => void;
  /**
   * Fired once the reconnect successfully completes its `hello` /
   * transport + producer re-setup.  UIs hide the "Reconnecting…"
   * overlay here.
   */
  onReconnected?: () => void;
  /**
   * Fires when any peer in the room sends a "live gift".  The
   * broadcaster UI renders a celebratory toast + updates the
   * running total of tips received in the broadcast summary.
   */
  onGift?: (gift: LiveGiftEvent) => void;
  /**
   * Fires when the server pushes a `live_tool_event` over the same
   * signaling channel.  See `liveToolsService.ts` for the payload
   * shape.  The handler is also auto-wired to dispatch through the
   * shared `liveToolsService` event bus, so subscribers attached
   * via `subscribeToLiveToolEvents` see the event regardless of
   * whether this callback is set.
   */
  onLiveToolEvent?: (event: import('./liveToolsService').LiveToolEvent) => void;
  /**
   * Fires when the server pushes a `live_agent_caption` over the
   * signaling channel — the AI-agent broadcast pipeline (slice 4e)
   * fans rendered captions into the room so connected operators
   * + viewers can render the caption text and decode + play the
   * audio mixed into the broadcast. The handler receives the raw
   * payload (text, audio bytes as base64, mime, duration). On a
   * broadcaster socket the most common caller binds this to
   * `engine.pushCaption({ text, audio, durationMs })` so every
   * peer hears the same audio at roughly the same wall-clock time.
   */
  onAgentCaption?: (caption: AgentCaptionEvent) => void;
}

export interface AgentCaptionEvent {
  /** Agent G-address that signed + sent the caption. */
  agent: string;
  /** Caption text (already capped at the backend's max_chars). */
  text: string;
  /** Azure voice id used for synthesis (e.g. en-US-JennyNeural). */
  voice: string;
  /** Mime type for the audio bytes. Default audio/mpeg. */
  mime: string;
  /** Audio duration as Azure reported it (milliseconds). */
  duration_ms: number;
  /** Rendered audio bytes, base64-encoded. */
  audio_base64: string;
  /** SHA-256 hex of the canonicalised caption text. */
  text_sha256: string;
  /** Server timestamp the caption was fanned out. */
  ts: number;
  /**
   * Set when the speaker is a spatial object rather than an agent — a
   * building, floor or zone talking on its own broadcast. The payload is
   * otherwise identical, deliberately: a listener decodes and plays the
   * audio the same way whoever is speaking, and forking the viewer into
   * two near-identical caption paths would buy nothing.
   */
  spatialObjectId?: string | null;
  /** Per-utterance id, so a cancel can name what it is cancelling. */
  utteranceId?: string | null;
  /** Where the words came from: 'ai', 'fallback', 'manual', a rule, … */
  source?: string | null;
}

/**
 * Normalise a caption fan-out into `AgentCaptionEvent`.
 *
 * Shared by the broadcaster and viewer sockets and by both the agent
 * (`live_agent_caption`) and spatial object (`spatial_object_caption`)
 * event names.
 */
export function normaliseCaptionEvent(msg: any): AgentCaptionEvent {
  return {
    agent:        String(msg.agent || msg.spatial_object_id || ''),
    text:         String(msg.text || ''),
    voice:        String(msg.voice || ''),
    mime:         String(msg.mime || 'audio/mpeg'),
    duration_ms:  Number(msg.duration_ms || 0),
    audio_base64: String(msg.audio_base64 || ''),
    text_sha256:  String(msg.text_sha256 || ''),
    ts:           Number(msg.ts || Date.now()),
    spatialObjectId: msg.spatial_object_id ? String(msg.spatial_object_id) : null,
    utteranceId:     msg.utterance_id ? String(msg.utterance_id) : null,
    source:          msg.source ? String(msg.source) : null,
  };
}

/**
 * Snapshot of the broadcaster's send-side WebRTC pipeline. Used by
 * the agent live modal to surface a "Media: live / connecting / failed"
 * badge so operators can tell whether the broadcaster is actually
 * pushing RTP, vs. only signaling being healthy. Without this, a
 * viewer-side ICE failure looks identical to a broadcaster-side one
 * — both manifest as "the viewer waited 20s and got NAT/firewall".
 */
export interface BroadcastStats {
  /** mediasoup transport state, mapped onto RTCIceConnectionState. */
  transportState: RTCIceConnectionState;
  /** True iff a video producer is currently established (regardless of pause state). */
  hasVideoProducer: boolean;
  /** True iff an audio producer is currently established. */
  hasAudioProducer: boolean;
  /**
   * Total bytes the video producer has sent over its lifetime, sampled
   * from `RTCRtpSender.getStats()`. Monotonically increasing while the
   * pipeline is healthy. Caveat: some browsers populate `packetsSent`
   * a few seconds before `bytesSent` shows up, so do not treat
   * `videoBytesSent === 0` alone as proof that nothing is flowing —
   * cross-check `videoPacketsSent` first.
   */
  videoBytesSent: number;
  /** Audio counterpart of `videoBytesSent`. Silent producers still emit small heartbeat packets, so this climbs slowly even when the agent isn't speaking. */
  audioBytesSent: number;
  /** Cumulative RTP packets sent on the video producer. Reliable "is this stream actually flowing?" signal across browsers. */
  videoPacketsSent: number;
  /** Audio counterpart of `videoPacketsSent`. */
  audioPacketsSent: number;
  /** ms since `startBroadcast()` ran. Useful for "should I be worried yet?" math. */
  ageMs: number;
}

export interface BroadcastController {
  broadcasterId: string;
  stop(): void;
  viewerCount(): number;
  /**
   * Poll the current broadcast pipeline state. Cheap to call (reads
   * cached counters; only `getStats()` round-trips into the browser's
   * WebRTC layer, and even that's measured in microseconds). Safe to
   * call before the transport has connected — returns a stats object
   * with `transportState: 'new'` and zero bytes.
   */
  getStats(): BroadcastStats;
  /**
   * Hot-swap the outgoing video (or audio) track across every connected
   * viewer without renegotiating the peer connection.  Used by the
   * front/rear camera flip and by devices that expose multiple cameras
   * the user wants to switch between during a live session.  The new
   * track must be from the currently broadcasting MediaStream — pass
   * `null` to drop the kind entirely.  Returns true if at least one
   * sender was updated.
   */
  replaceTrack(kind: 'video' | 'audio', newTrack: MediaStreamTrack | null): boolean;
  /**
   * Pause or resume the broadcast without tearing down the session.
   *
   * On the SFU path this maps to `producer.pause()` / `producer.resume()`
   * for both audio and video producers, stopping the outbound media
   * upstream (SFU) so bandwidth drops to signaling-only while paused.
   *
   * On the legacy mesh path we toggle `track.enabled` on the outgoing
   * tracks — browsers then replace the signal with a black frame for
   * video and silence for audio, which the viewer UI masks with a
   * "Broadcast paused" overlay driven by the `broadcast_paused` /
   * `broadcast_resumed` signaling events the server forwards.
   *
   * Returns true when the new state was applied, false if the
   * broadcast isn't ready (no active tracks, transport not
   * connected, etc.) so the UI can fall back to a notice.
   */
  setPaused(paused: boolean): boolean;
  /**
   * Announce a live gift *from the broadcaster* to their own room
   * (rarely used — mostly viewers send).  Mirrors the viewer's
   * `sendGift`.  Returns true when the message was queued.
   */
  sendGift(gift: SendGiftPayload): boolean;
}

/**
 * Decorative "live gift" event — one viewer sent a trustline asset
 * as a tip.  The server forwards this verbatim to every peer in
 * the room (including the broadcaster) so the UI can render a
 * congratulatory toast/animation.  The on-chain payment itself is
 * performed by the sender's wallet BEFORE the signaling message is
 * emitted; we do not verify the transaction server-side — the
 * `txHash` is included purely so recipients can sanity-check it
 * against Horizon if they want.
 */
export interface LiveGiftEvent {
  /** Peer that sent the gift (stable per-session id). */
  fromPeerId: string;
  /** Sender's wallet address (G… or C…) when the hub knows it. */
  fromWallet: string | null;
  /** Classic asset code — `'XLM'` for native.  Never contract-scoped. */
  assetCode: string;
  /** Issuer G-address for non-native assets.  `null` for XLM. */
  assetIssuer: string | null;
  /** Amount as a Stellar amount string (up to 7 decimals). */
  amount: string;
  /** Optional free-form note shown on the toast (≤ 120 chars). */
  memo: string;
  /** Horizon tx hash when the sender ran the payment through Horizon. */
  txHash: string | null;
  /** Server timestamp (ms) — use for sort order, NOT trust. */
  sentAt: number;
}

/**
 * Payload for `sendGift` on both the broadcaster and viewer
 * controllers.  Mirrors `LiveGiftEvent` but without server-assigned
 * fields (`fromPeerId`, `sentAt`).
 */
export interface SendGiftPayload {
  assetCode: string;
  assetIssuer?: string | null;
  amount: string;
  memo?: string;
  txHash?: string | null;
}

export function startBroadcast(stream: MediaStream, opts: BroadcasterOpts): BroadcastController {
  // If the frontend is configured to route through the SFU, delegate
  // to sfuClient.  The SFU driver exposes an identical
  // BroadcastController, so every call site (ProfileOverlay,
  // LiveFab, etc.) works transparently.  Importing lazily avoids
  // pulling in the mediasoup-client bundle on SFU-off builds.
  if (isSfuEnabled()) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { startBroadcastSfu } = require('./sfuClient') as typeof import('./sfuClient');
    return startBroadcastSfu(stream, opts);
  }

  const url = wsUrl(opts.backendUrl || DEFAULT_BACKEND);
  if (!url) {
    opts.onError?.(new Error('no_ws_url'));
    return {
      broadcasterId: opts.broadcasterId,
      stop() {},
      viewerCount() { return 0; },
      getStats() {
        return {
          transportState: 'closed' as RTCIceConnectionState,
          hasVideoProducer: false,
          hasAudioProducer: false,
          videoBytesSent: 0,
          audioBytesSent: 0,
          videoPacketsSent: 0,
          audioPacketsSent: 0,
          ageMs: 0,
        };
      },
      replaceTrack() { return false; },
      setPaused() { return false; },
      sendGift() { return false; },
    };
  }

  const ws = new WebSocket(url);
  const perViewer = new Map<string, RTCPeerConnection>();
  // Set by `stop()` so terminal events (ws error/close, pc state change)
  // emitted as a side effect of teardown are not reported back to the
  // caller as errors.  Intentional stops should be silent.
  let stopped = false;

  // Mutable reference to the track we're *currently* sending for each
  // kind.  New viewers are added with these tracks, not the ones that
  // were passed in at startBroadcast time — so a camera flip before a
  // second viewer joins is picked up transparently.
  const currentTracks: { video: MediaStreamTrack | null; audio: MediaStreamTrack | null } = {
    video: stream.getVideoTracks()[0] || null,
    audio: stream.getAudioTracks()[0] || null,
  };

  ws.addEventListener('open', () => {
    if (stopped) return;
    ws.send(JSON.stringify({
      type: 'hello', role: 'broadcaster', broadcasterId: opts.broadcasterId,
      wallet: opts.wallet, country: opts.country,
      liveSessionId: opts.liveSessionId, grantToken: opts.grantToken,
    }));
  });

  async function handleViewer(peerId: string) {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    perViewer.set(peerId, pc);
    // Always use the freshest tracks — a camera flip that happened
    // before this viewer joined would otherwise silently revert.
    if (currentTracks.video) pc.addTrack(currentTracks.video, stream);
    if (currentTracks.audio) pc.addTrack(currentTracks.audio, stream);
    // Nudge the offer toward H.264 — iOS Safari viewers see black
    // frames when Chrome offers VP8/VP9 first even though the track
    // nominally negotiates, so we always prefer the codec with the
    // best cross-platform hardware decode (H.264).  No-op on browsers
    // that lack H.264 encode (rare on Windows/Mac, possible on Linux
    // Chromium) — the preference quietly falls back to VP8 and
    // nothing changes vs. today's behaviour.
    applyH264Preference(pc, `broadcaster→viewer(${peerId})`);
    pc.onicecandidate = (ev) => {
      if (ev.candidate) ws.send(JSON.stringify({ type: 'ice', to: peerId, candidate: ev.candidate }));
    };
    pc.onconnectionstatechange = () => {
      if (stopped) return;
      // Surface to the console so operators can see NAT/ICE churn per
      // viewer during a session.  The UI doesn't need to reflect per-
      // viewer state — the server's viewer-count update already does.
      if (['disconnected', 'failed', 'closed'].includes(pc.connectionState)) {
        pc.close();
        perViewer.delete(peerId);
        opts.onViewerLeft?.(peerId);
      }
    };
    pc.oniceconnectionstatechange = () => {
      if (stopped) return;
    };
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      ws.send(JSON.stringify({ type: 'offer', to: peerId, sdp: pc.localDescription }));
    } catch (err: any) {
      opts.onError?.(new Error(err?.message || 'broadcaster_offer_failed'));
    }
  }

  ws.addEventListener('message', (ev) => {
    let msg: any;
    try { msg = JSON.parse(String(ev.data)); } catch { return; }
    if (!msg?.type) return;
    switch (msg.type) {
      case 'denied': opts.onDenied?.(msg.reason || 'denied'); break;
      case 'session_ended':
        opts.onSessionEnded?.(msg.reason || 'ended');
        break;
      case 'session_extended':
        if (typeof msg.scheduledEndAt === 'string') {
          opts.onSessionExtended?.({
            scheduledEndAt: msg.scheduledEndAt,
            extensionSeconds: msg.extensionSeconds,
            extensionCount: msg.extensionCount,
            fromViewer: msg.fromViewer,
          });
        }
        break;
      case 'viewers_update':
        if (typeof msg.count === 'number') opts.onViewersUpdate?.(msg.count);
        break;
      case 'joined':
        if (typeof msg.peerId === 'string') {
          handleViewer(msg.peerId);
          opts.onViewerJoined?.({
            peerId: msg.peerId,
            wallet: typeof msg.wallet === 'string' ? msg.wallet : undefined,
            country: typeof msg.country === 'number' ? msg.country : null,
          });
        }
        break;
      case 'left':
        if (typeof msg.peerId === 'string') {
          const pc = perViewer.get(msg.peerId);
          pc?.close();
          perViewer.delete(msg.peerId);
          opts.onViewerLeft?.(msg.peerId);
        }
        break;
      case 'answer': {
        const pc = perViewer.get(msg.from);
        if (pc && msg.sdp) pc.setRemoteDescription(msg.sdp).catch((err) => opts.onError?.(err));
        break;
      }
      case 'ice': {
        const pc = perViewer.get(msg.from);
        if (pc && msg.candidate) pc.addIceCandidate(msg.candidate).catch(() => { /* ignore */ });
        break;
      }
      case 'live_gift': {
        try {
          opts.onGift?.({
            fromPeerId: String(msg.fromPeerId || ''),
            fromWallet: typeof msg.fromWallet === 'string' ? msg.fromWallet : null,
            assetCode: typeof msg.assetCode === 'string' ? msg.assetCode : 'XLM',
            assetIssuer: typeof msg.assetIssuer === 'string' ? msg.assetIssuer : null,
            amount: String(msg.amount || ''),
            memo: typeof msg.memo === 'string' ? msg.memo : '',
            txHash: typeof msg.txHash === 'string' ? msg.txHash : null,
            sentAt: typeof msg.sentAt === 'number' ? msg.sentAt : Date.now(),
          });
        } catch { /* ignore */ }
        break;
      }
      case 'live_tool_event': {
        // Tool fan-out — dispatch through both the optional callback
        // and the global liveToolsService event bus so any component
        // (broadcaster control panel, viewer overlay) can subscribe
        // independently without each path knowing about the others.
        try {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const liveTools = require('./liveToolsService') as typeof import('./liveToolsService');
          const ev = {
            event: msg.event,
            sessionId: msg.sessionId,
            toolId: msg.toolId ?? null,
            tool: msg.tool ?? null,
            snapshot: msg.snapshot,
            error: msg.error,
          } as import('./liveToolsService').LiveToolEvent;
          opts.onLiveToolEvent?.(ev);
          liveTools.dispatchLiveToolEvent(ev);
        } catch { /* never let a tool event crash the WS handler */ }
        break;
      }
      case 'live_agent_caption':
      case 'spatial_object_caption': {
        // AI agent broadcast caption fan-out (slice 4e), and the
        // spatial object equivalent. The same payload is pushed to
        // broadcaster + every viewer so captions and audio are
        // aligned across the room.
        try {
          opts.onAgentCaption?.(normaliseCaptionEvent(msg));
        } catch { /* never let a caption event crash the WS handler */ }
        break;
      }
      default: break;
    }
  });

  ws.addEventListener('error', () => {
    if (stopped) return;
    opts.onError?.(new Error('webrtc_ws_error'));
  });
  ws.addEventListener('close', () => {
    if (stopped) return;
    // Connection closed unexpectedly (not by our stop()).  Surface as a
    // lifecycle-ended signal rather than an error so the UI can render a
    // friendly "socket_closed" instead of a red error banner.
    opts.onSessionEnded?.('socket_closed');
  });

  const startedAt = Date.now();
  const meshStats = (): BroadcastStats => {
    let aggregate: RTCIceConnectionState = 'new';
    let video = false;
    let audio = false;
    perViewer.forEach((pc) => {
      const s = pc.iceConnectionState;
      if (s === 'connected' || s === 'completed') aggregate = 'connected';
      else if (aggregate === 'new') aggregate = s;
      pc.getSenders().forEach((snd) => {
        if (snd.track?.kind === 'video') video = true;
        if (snd.track?.kind === 'audio') audio = true;
      });
    });
    return {
      transportState: aggregate,
      hasVideoProducer: video,
      hasAudioProducer: audio,
      videoBytesSent: 0,
      audioBytesSent: 0,
      videoPacketsSent: 0,
      audioPacketsSent: 0,
      ageMs: Date.now() - startedAt,
    };
  };

  return {
    broadcasterId: opts.broadcasterId,
    viewerCount: () => perViewer.size,
    getStats: meshStats,
    replaceTrack(kind, newTrack) {
      currentTracks[kind] = newTrack;
      let updated = 0;
      perViewer.forEach((pc) => {
        // Match by transceiver: senders we added always have a sibling
        // receiver of the same kind, so this is robust even if the
        // current track was previously replaced with null.
        const transceiver = pc.getTransceivers().find((t) =>
          t.sender && (t.sender.track?.kind === kind || t.receiver?.track?.kind === kind));
        const sender = transceiver?.sender;
        if (sender) {
          sender.replaceTrack(newTrack).catch((err) => {
          });
          updated += 1;
        }
      });
      // Even with zero current viewers we still record the swap into
      // currentTracks so the *next* viewer to join gets the new track.
      return updated > 0 || perViewer.size === 0;
    },
    stop() {
      stopped = true;
      try { ws.close(); } catch { /* ignore */ }
      perViewer.forEach((pc) => { try { pc.close(); } catch { /* ignore */ } });
      perViewer.clear();
    },
    setPaused(paused: boolean) {
      // Mesh path: flip `track.enabled` which makes the browser
      // send black frames / silence without tearing down the
      // peer connection.  Then fan out a `broadcast_paused` /
      // `broadcast_resumed` signaling message through the hub so
      // every viewer gets a deterministic overlay state — driving
      // it purely off incoming frames would be racy on Safari
      // (which can take 2-3s to detect the frozen stream).
      const video = currentTracks.video;
      const audio = currentTracks.audio;
      if (!video && !audio) return false;
      try { if (video) video.enabled = !paused; } catch { /* ignore */ }
      try { if (audio) audio.enabled = !paused; } catch { /* ignore */ }
      try {
        ws.send(JSON.stringify({
          type: paused ? 'broadcast_paused' : 'broadcast_resumed',
        }));
      } catch { /* ignore */ }
      return true;
    },
    sendGift(gift: SendGiftPayload) {
      if (ws.readyState !== WebSocket.OPEN) return false;
      try {
        ws.send(JSON.stringify({
          type: 'live_gift',
          assetCode: gift.assetCode,
          assetIssuer: gift.assetIssuer || null,
          amount: gift.amount,
          memo: gift.memo || '',
          txHash: gift.txHash || null,
        }));
        return true;
      } catch {
        return false;
      }
    },
  };
}

export interface ViewerOpts {
  broadcasterId: string;
  wallet?: string;
  country?: number;
  backendUrl?: string;
  /**
   * ICE transport policy override.  When set to `'relay'`, ICE
   * skips host / server-reflexive candidates and only considers
   * TURN-relay candidates.  Use this as a retry path for viewers
   * whose first attempt failed on strict 5G / corporate NAT where
   * direct UDP never reaches the broadcaster — forcing relay mode
   * short-circuits the pointless candidate pair checks and usually
   * connects within a few seconds of ICE gathering.
   *
   * Default = `'all'` (browser default).
   */
  iceTransportPolicy?: RTCIceTransportPolicy;
  /**
   * Streamed out every time the connection's ICE / WS / codec state
   * changes.  The UI holds a snapshot of the last value so an
   * "ended" card can render a "Show technical details" panel —
   * critically useful when the viewer is on a phone with no
   * browser console (iPhone Safari) and we're debugging over chat.
   * The payload intentionally avoids PII beyond candidate *types*
   * (host / srflx / relay) — no IP addresses or ports are surfaced.
   */
  onDiagnostics?: (diag: ViewerDiagnostics) => void;
  onStream: (stream: MediaStream) => void;
  onDenied?: (reason: string) => void;
  onError?: (err: Error) => void;
  /**
   * Fired when the session ends for any reason.  `reason` is either a
   * freeform network close ('socket_closed') or the server-reported
   * ended reason (e.g. 'ended_duration', 'ended_user').  The viewer UI
   * should render a friendly ended state instead of a raw error.
   */
  onEnded?: (reason?: string) => void;
  /** Mirrors the broadcaster's `onSessionExtended` — pushed to every peer in the room. */
  onSessionExtended?: (info: {
    scheduledEndAt: string;
    extensionSeconds?: number;
    extensionCount?: number;
    fromViewer?: boolean;
  }) => void;
  /** Mirrors the broadcaster's `onViewersUpdate` — the authoritative
   *  count of viewers currently connected to the room. */
  onViewersUpdate?: (count: number) => void;

  /**
   * Reconnect hooks mirroring the broadcaster side.  When `shouldResume`
   * returns true and the signaling socket drops unexpectedly, the
   * service reopens the ws, re-runs `hello` (as a viewer), and — in
   * the SFU path — re-consumes every producer advertised by the
   * server.  The viewer's `<video>` is fed from the same aggregate
   * MediaStream across reconnects, so the element itself doesn't
   * remount.
   *
   * The server's broadcaster-grace window means a broadcaster blip
   * commonly appears to a viewer as "tracks ended, socket ok, a new
   * producer lands 5s later".  In that case `shouldResume` never runs
   * because the viewer socket never closed — the stream just updates
   * in-place.  Reconnect fires when the VIEWER's own network drops.
   */
  shouldResume?: () => boolean;
  onReconnecting?: (attempt: number) => void;
  onReconnected?: () => void;
  /**
   * Viewer-specific: fired when the server announces the broadcaster
   * temporarily disconnected (grace window active).  Let the UI show
   * a "Broadcaster reconnecting…" pill over the last frame.
   */
  onBroadcasterDisconnected?: (graceMs: number) => void;
  /**
   * Viewer-specific: fired when the broadcaster successfully
   * reclaimed the room.  Clears the disconnect pill.
   */
  onBroadcasterReconnected?: () => void;
  /**
   * Viewer-specific: fired when the broadcaster announces a
   * deliberate pause (via the `setPaused(true)` controller
   * method).  The server re-broadcasts a `broadcast_paused`
   * event to every viewer so they can render a "Paused" pill
   * over the frozen last frame.
   */
  onBroadcastPaused?: () => void;
  /**
   * Viewer-specific: fired when the broadcaster resumes.
   * Clears the paused overlay.
   */
  onBroadcastResumed?: () => void;
  /**
   * Fired when the backend fans out a `live_gift` event — a viewer
   * in the same room sent the broadcaster an on-chain tip.  Both
   * the broadcaster and every other viewer receive these so the UI
   * can display a celebratory toast.  Senders also see their own
   * event reflected back so they can confirm the broadcaster saw it.
   */
  onGift?: (gift: LiveGiftEvent) => void;
  /**
   * Fires when the server pushes a `live_tool_event` for any reason
   * (snapshot delivery on join, tool added/updated/removed, value
   * refreshed, etc).  The event is also dispatched to the shared
   * `liveToolsService` event bus.  Late-joining viewers receive a
   * single `tool.snapshot` event before any individual updates.
   */
  onLiveToolEvent?: (event: import('./liveToolsService').LiveToolEvent) => void;
  /**
   * Fires when the server pushes a `live_agent_caption` event into
   * the room (slice 4e). Viewers typically render the caption text
   * in a subtitle band overlay and decode + play `audio_base64` so
   * the agent's voice is heard alongside the synthetic video track
   * the broadcaster's canvas is producing.
   */
  onAgentCaption?: (caption: AgentCaptionEvent) => void;
  /**
   * Fires when a spatial object interrupts itself. The audio for the
   * named utterance is already in flight, so the listener should stop
   * playing it rather than let two lines overlap.
   */
  onCaptionCancel?: (cancel: {
    spatialObjectId: string;
    utteranceId: string | null;
    reason: string;
  }) => void;
  /**
   * Marks this viewer as attached to an active teleport session
   * (i.e. the user is watching the broadcast inside the cinematic
   * teleport HUD, not just from the profile overlay). When at
   * least one teleport-attached viewer is in the room AND the
   * country policy allows it, the server auto-extends the
   * broadcast's scheduled end time in 60s chunks (capped at
   * 30 minutes total free extension) so the broadcaster doesn't
   * get cut mid-cinematic. The host pays nothing.
   *
   * Default false — the existing ProfileOverlay viewer flow does
   * not opt in. Only the AgentTeleportLivePlayer sets this true.
   */
  teleportAttached?: boolean;
  /**
   * Short-lived viewer token for trust-group-restricted broadcasts.
   * Obtained from `POST /api/live-sessions/:id/viewer-grant` after the
   * viewer proves wallet ownership (SEP-10 challenge). Omitted for
   * public broadcasts — the hub only requires it for group-scoped rooms.
   */
  viewerToken?: string | null;
  /**
   * Fires once after the server's `welcome` (and again from any
   * subsequent `sfu-list-producers` response) telling the viewer
   * how the broadcaster intends to deliver media:
   *
   *   - `'media'`           — RTCPeerConnection / SFU producers, the
   *                           default for every browser-driven
   *                           broadcast. Viewer should render the
   *                           inbound MediaStream the way it always
   *                           has.
   *   - `'signaling_only'`  — the broadcaster is the headless MCP /
   *                           geoai `start_agent_live` peer: it
   *                           keeps the WebSocket open so REST
   *                           `agent_speak?broadcast=true` can fan
   *                           out `live_agent_caption` events, but
   *                           never produces RTP. Viewers should
   *                           skip waiting for inbound video and
   *                           render an avatar+captions canvas
   *                           locally instead, otherwise their
   *                           recv-transport never converges and
   *                           they get a misleading "Media path did
   *                           not connect (NAT/firewall)" timeout.
   *
   * The SFU client also short-circuits its own ICE watchdog when the
   * mode is `signaling_only`, so a missing `onMode` handler still
   * means "no false-positive ice_timeout" — the callback only exists
   * so the UI can decide *what to render* instead.
   */
  onMode?: (mode: BroadcasterMode) => void;
}

/**
 * Declared by the broadcaster on `hello` and surfaced to viewers
 * via the `welcome` (and `sfu-producers`) message. Determines
 * whether the viewer pipeline should expect RTP or render a
 * captions-only canvas locally. See `ViewerOpts.onMode`.
 */
export type BroadcasterMode = 'media' | 'signaling_only';

export interface ViewerController {
  broadcasterId: string;
  stop(): void;
  /**
   * Announce a "live gift" to the room.  The caller must first
   * complete the on-chain payment (typically a Horizon classic
   * payment from the viewer's wallet to the broadcaster's wallet);
   * this method only fans out the decorative notification so the
   * broadcaster + other viewers can render the tip animation.
   * Returns true when the signaling message was queued.
   */
  sendGift(gift: SendGiftPayload): boolean;
}

/**
 * Privacy-safe debugging snapshot of a running viewer connection.
 * Emitted on every meaningful state change so the ProfileOverlay can
 * render a collapsible "Show connection details" block in the error
 * card — essential when diagnosing iOS Safari issues remotely since
 * iPhone Safari has no accessible devtools console for most users.
 */
export interface ViewerDiagnostics {
  /** WebSocket readyState as a human string. */
  wsState: 'connecting' | 'open' | 'closing' | 'closed' | 'unknown';
  /** ICE connection state (`new` → `checking` → `connected` / `failed`). */
  iceState: RTCIceConnectionState;
  /** Peer-connection aggregate state. */
  pcState: RTCPeerConnectionState;
  /** ICE gathering state — `complete` means we stopped trickling. */
  iceGatheringState: RTCIceGatheringState;
  /** Which candidate types our side has gathered so far. */
  localCandidateTypes: ('host' | 'srflx' | 'prflx' | 'relay' | 'mdns')[];
  /** Which candidate types we have RECEIVED from the broadcaster. */
  remoteCandidateTypes: ('host' | 'srflx' | 'prflx' | 'relay' | 'mdns')[];
  /** Policy we're running ICE under (`all` or `relay`). */
  iceTransportPolicy: RTCIceTransportPolicy;
  /** Milliseconds since the viewer was started. */
  elapsedMs: number;
}

export function joinBroadcast(opts: ViewerOpts): ViewerController {
  // SFU delegation — see note in startBroadcast.
  if (isSfuEnabled()) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { joinBroadcastSfu } = require('./sfuClient') as typeof import('./sfuClient');
    return joinBroadcastSfu(opts);
  }

  const url = wsUrl(opts.backendUrl || DEFAULT_BACKEND);
  if (!url) {
    opts.onError?.(new Error('no_ws_url'));
    return { broadcasterId: opts.broadcasterId, stop() {}, sendGift() { return false; } };
  }

  const ws = new WebSocket(url);
  const iceTransportPolicy: RTCIceTransportPolicy = opts.iceTransportPolicy || 'all';
  const pc = new RTCPeerConnection({
    iceServers: ICE_SERVERS,
    iceTransportPolicy,
  });
  let broadcasterPeerId: string | null = null;
  let stopped = false;
  let iceReached = false;

  // Diagnostics snapshot fed to `opts.onDiagnostics` on every
  // meaningful state transition.  Candidate types are tracked as
  // sets to avoid duplicates as ICE trickles.  This is the *only*
  // window we get into what failed when an iPhone viewer reports
  // "black screen" since iOS Safari doesn't expose devtools.
  const startedAt = Date.now();
  const localCandidateTypes = new Set<ViewerDiagnostics['localCandidateTypes'][number]>();
  const remoteCandidateTypes = new Set<ViewerDiagnostics['remoteCandidateTypes'][number]>();
  const wsStateString = (): ViewerDiagnostics['wsState'] => {
    switch (ws.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN:       return 'open';
      case WebSocket.CLOSING:    return 'closing';
      case WebSocket.CLOSED:     return 'closed';
      default:                   return 'unknown';
    }
  };
  const emitDiag = () => {
    if (!opts.onDiagnostics) return;
    try {
      opts.onDiagnostics({
        wsState: wsStateString(),
        iceState: pc.iceConnectionState,
        pcState: pc.connectionState,
        iceGatheringState: pc.iceGatheringState,
        localCandidateTypes:  Array.from(localCandidateTypes),
        remoteCandidateTypes: Array.from(remoteCandidateTypes),
        iceTransportPolicy,
        elapsedMs: Date.now() - startedAt,
      });
    } catch { /* never let diagnostics crash the pipeline */ }
  };
  // Classify a candidate string returned by the browser.  The SDP
  // `typ` keyword tells us host / srflx / prflx / relay; Chromium
  // replaces raw host IPs with `.local` mDNS names for privacy so
  // we surface that as its own bucket for operator visibility.
  const classifyCandidate = (cand: string | null | undefined):
    ViewerDiagnostics['localCandidateTypes'][number] | null => {
    if (!cand) return null;
    const typ = /typ\s+(host|srflx|prflx|relay)/.exec(cand)?.[1];
    if (typ === 'host' && /\.local\s+/.test(cand)) return 'mdns';
    if (typ === 'host' || typ === 'srflx' || typ === 'prflx' || typ === 'relay') return typ;
    return null;
  };

  // Pre-declare recvonly transceivers so the answer we generate always
  // advertises both audio and video m-lines even if, for any reason, the
  // offer arrives before getUserMedia-style setup.  This makes ontrack
  // fire reliably across Chromium/Firefox/Safari.
  try { pc.addTransceiver('video', { direction: 'recvonly' }); } catch {}
  try { pc.addTransceiver('audio', { direction: 'recvonly' }); } catch {}
  // Prefer H.264 in the answer.  Because iOS Safari advertises H.264
  // first natively, this only really matters for Chromium / Firefox
  // viewers talking to an iOS broadcaster — it ensures they send an
  // SDP answer that picks H.264 instead of negotiating down to VP8,
  // matching the broadcaster's hardware encoder output exactly.
  applyH264Preference(pc, 'viewer←broadcaster');

  pc.ontrack = (ev) => {
    if (stopped) return;
    opts.onStream(ev.streams[0]);
  };
  pc.onicecandidate = (ev) => {
    if (stopped) return;
    if (ev.candidate) {
      const kind = classifyCandidate(ev.candidate.candidate);
      if (kind) {
        localCandidateTypes.add(kind);
        emitDiag();
      }
    }
    if (ev.candidate && broadcasterPeerId) {
      ws.send(JSON.stringify({ type: 'ice', to: broadcasterPeerId, candidate: ev.candidate }));
    }
  };
  pc.onicegatheringstatechange = () => {
    if (stopped) return;
    emitDiag();
  };
  pc.oniceconnectionstatechange = () => {
    if (stopped) return;
    if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
      iceReached = true;
    }
    if (pc.iceConnectionState === 'failed') {
      opts.onEnded?.('ice_failed');
    }
    emitDiag();
  };
  pc.onconnectionstatechange = () => {
    if (stopped) return;
    if (['disconnected', 'failed', 'closed'].includes(pc.connectionState)) {
      opts.onEnded?.('connection_state_' + pc.connectionState);
    }
    emitDiag();
  };

  // Watchdog: if we never transition out of 'checking'/'new' within the
  // timeout window, the media path never came up — surface a specific
  // `ice_timeout` so the UI can render a "couldn't reach the broadcaster"
  // message instead of a silent black frame.
  //
  // SIGNALING-ONLY broadcasters (the headless geoai MCP "start_agent_live"
  // peer) intentionally never produce RTP — the room only relays REST-
  // injected `live_agent_caption` events. Without the cancel call below,
  // viewers ALWAYS hit this watchdog 15s in and got a misleading
  // "Media path did not connect (NAT/firewall)" toast for an AI agent
  // that was working perfectly. The `welcome` case in the message switch
  // detects `broadcasterMode === 'signaling_only'` and cancels this
  // watchdog before it can fire.
  let iceWatchdog: ReturnType<typeof setTimeout> | null = setTimeout(() => {
    if (stopped || iceReached) return;
    opts.onEnded?.('ice_timeout');
  }, VIEWER_ICE_TIMEOUT_MS);
  const cancelIceWatchdog = () => {
    if (iceWatchdog != null) {
      clearTimeout(iceWatchdog);
      iceWatchdog = null;
    }
  };

  ws.addEventListener('open', () => {
    if (stopped) return;
    emitDiag();
    ws.send(JSON.stringify({
      type: 'hello', role: 'viewer', broadcasterId: opts.broadcasterId,
      wallet: opts.wallet, country: opts.country,
      // Only sent when truthy so existing viewers continue to send
      // exactly the same shape as before — backend hub already
      // tolerates unknown fields, but keeping the wire format
      // minimal makes the change safe to roll back unilaterally.
      ...(opts.teleportAttached ? { teleportAttached: true } : {}),
      ...(opts.viewerToken ? { viewerToken: opts.viewerToken } : {}),
    }));
  });

  ws.addEventListener('message', async (ev) => {
    let msg: any;
    try { msg = JSON.parse(String(ev.data)); } catch { return; }
    if (!msg?.type) return;
    switch (msg.type) {
      case 'welcome': {
        // The hub sends `welcome` with `broadcasterMode` so the viewer
        // knows whether to expect an RTP stream ('media') or whether
        // the broadcaster is a headless signaling-only participant
        // (the geoai MCP `start_agent_live` peer that fans out
        // `live_agent_caption` events but never produces video/audio
        // tracks). Without this branch, signaling-only AI agents:
        //   1. never produce an SDP offer → recv transceivers idle,
        //   2. ICE never converges → 15s watchdog fires → viewer
        //      sees "Media path did not connect (NAT/firewall)",
        //   3. viewer ALSO never sees the synthetic avatar canvas
        //      because `onMode('signaling_only')` never fired in the
        //      P2P path (only in sfuClient's welcome handler).
        // The fix here mirrors sfuClient: tell the caller the mode
        // (so ProfileOverlay can spin up the synthetic canvas
        // engine + draw the agent avatar locally) and cancel the
        // watchdog so we don't blow up a perfectly-functioning
        // signaling-only session 15s in.
        const rawMode = msg.broadcasterMode === 'signaling_only' ? 'signaling_only' : 'media';
        try { opts.onMode?.(rawMode as BroadcasterMode); } catch { /* swallow */ }
        if (rawMode === 'signaling_only') {
          cancelIceWatchdog();
        }
        break;
      }
      case 'denied': opts.onDenied?.(msg.reason || 'denied'); ws.close(); break;
      case 'session_ended':
        opts.onEnded?.(msg.reason || 'ended');
        try { ws.close(); } catch { /* ignore */ }
        break;
      case 'session_extended':
        if (typeof msg.scheduledEndAt === 'string') {
          opts.onSessionExtended?.({
            scheduledEndAt: msg.scheduledEndAt,
            extensionSeconds: msg.extensionSeconds,
            extensionCount: msg.extensionCount,
            fromViewer: msg.fromViewer,
          });
        }
        break;
      case 'viewers_update':
        if (typeof msg.count === 'number') opts.onViewersUpdate?.(msg.count);
        break;
      case 'broadcast_paused':
        opts.onBroadcastPaused?.();
        break;
      case 'broadcast_resumed':
        opts.onBroadcastResumed?.();
        break;
      case 'live_gift':
        try {
          opts.onGift?.({
            fromPeerId: String(msg.fromPeerId || ''),
            fromWallet: typeof msg.fromWallet === 'string' ? msg.fromWallet : null,
            assetCode: typeof msg.assetCode === 'string' ? msg.assetCode : 'XLM',
            assetIssuer: typeof msg.assetIssuer === 'string' ? msg.assetIssuer : null,
            amount: String(msg.amount || ''),
            memo: typeof msg.memo === 'string' ? msg.memo : '',
            txHash: typeof msg.txHash === 'string' ? msg.txHash : null,
            sentAt: typeof msg.sentAt === 'number' ? msg.sentAt : Date.now(),
          });
        } catch { /* ignore */ }
        break;
      case 'live_tool_event':
        try {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const liveTools = require('./liveToolsService') as typeof import('./liveToolsService');
          const ev = {
            event: msg.event,
            sessionId: msg.sessionId,
            toolId: msg.toolId ?? null,
            tool: msg.tool ?? null,
            snapshot: msg.snapshot,
            error: msg.error,
          } as import('./liveToolsService').LiveToolEvent;
          opts.onLiveToolEvent?.(ev);
          liveTools.dispatchLiveToolEvent(ev);
        } catch { /* never let a tool event crash the WS handler */ }
        break;
      case 'live_agent_caption':
      case 'spatial_object_caption':
        try {
          opts.onAgentCaption?.(normaliseCaptionEvent(msg));
        } catch { /* never let a caption event crash the WS handler */ }
        break;
      case 'spatial_object_caption_cancel':
        // An interrupt superseded an utterance whose audio is already
        // playing. Nothing can recall the bytes, so the listener is
        // told to stop rather than left talking over the new line.
        try {
          opts.onCaptionCancel?.({
            spatialObjectId: String(msg.spatial_object_id || ''),
            utteranceId: msg.utterance_id ? String(msg.utterance_id) : null,
            reason: String(msg.reason || 'cancelled'),
          });
        } catch { /* never let a cancel event crash the WS handler */ }
        break;
      case 'offer':
        broadcasterPeerId = msg.from;
        try {
          await pc.setRemoteDescription(msg.sdp);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          ws.send(JSON.stringify({ type: 'answer', to: msg.from, sdp: pc.localDescription }));
        } catch (err: any) {
          opts.onError?.(new Error(err?.message || 'viewer_answer_failed'));
        }
        break;
      case 'ice':
        if (msg.candidate) {
          const kind = classifyCandidate(msg.candidate?.candidate);
          if (kind) {
            remoteCandidateTypes.add(kind);
            emitDiag();
          }
          pc.addIceCandidate(msg.candidate).catch(() => {});
        }
        break;
      case 'left':
        if (msg.role === 'broadcaster') { opts.onEnded?.('broadcaster_left'); ws.close(); }
        break;
      default: break;
    }
  });

  ws.addEventListener('error', () => {
    if (stopped) return;
    emitDiag();
    opts.onError?.(new Error('webrtc_ws_error'));
  });
  ws.addEventListener('close', () => {
    if (stopped) return;
    emitDiag();
    opts.onEnded?.('socket_closed');
  });

  return {
    broadcasterId: opts.broadcasterId,
    stop() {
      stopped = true;
      // cancelIceWatchdog() also handles the null-after-cancel case
      // (signaling-only paths null it out via the welcome handler);
      // the try/catch is belt-and-suspenders for the rare case where
      // the timer ID is still valid but Node's clearTimeout types
      // disagree with the runtime value.
      cancelIceWatchdog();
      try { ws.close(); } catch { /* ignore */ }
      try { pc.close(); } catch { /* ignore */ }
    },
    sendGift(gift: SendGiftPayload) {
      if (ws.readyState !== WebSocket.OPEN) return false;
      try {
        ws.send(JSON.stringify({
          type: 'live_gift',
          assetCode: gift.assetCode,
          assetIssuer: gift.assetIssuer || null,
          amount: gift.amount,
          memo: gift.memo || '',
          txHash: gift.txHash || null,
        }));
        return true;
      } catch {
        return false;
      }
    },
  };
}
